import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormGroup, FormControl, Validators, FormBuilder } from '@angular/forms';

import { Store } from '@ngrx/store';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { PASSWORD_PATTERN } from 'modules/shared/constants/password-pattern.constants';
import { MFA_CODE_PATTERN } from 'modules/shared/constants/mfa-code-pattern.constants';
import { IResetPassword } from 'modules/shared/models/auth.models';
import { SnackbarService } from 'app/services/snackbar.service';

import { AppState, Selectors } from 'store/root-reducer';
import * as AuthActions from 'store/actions/auth.actions';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'artemis-forgot-password',
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.scss']
})
export class ForgotPasswordComponent implements OnInit, OnDestroy {

  private passwordPattern: string =  PASSWORD_PATTERN;
  private mfaCodePattern: string = MFA_CODE_PATTERN;
  private unsubscribe$: ReplaySubject<any> = new ReplaySubject<any>(1);

  forgotPasswordForm: FormGroup;
  resetPasswordForm: FormGroup;
  isReset: boolean;

  constructor(private store: Store<AppState>,
              private formBuilder: FormBuilder,
              private translateService: TranslateService,
              private snackbar: SnackbarService) { }

  /**
   * Angular lifecycle hook
   * @method ngOnInit
   */
  ngOnInit() {
    this.buildForms();
    this.subscribeForgotPassword();
  }

  ngOnDestroy() {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();

    this.store.dispatch(new AuthActions.ClearForgotPassword());
    this.store.dispatch(new AuthActions.ClearResetPassword());
  }

  /**
   * Build form for forgot password page
   * @method buildForm
   */
  private buildForms(): void {
    this.forgotPasswordForm = this.formBuilder.group({
      username: new FormControl('', [Validators.email, Validators.required])
    });

    this.resetPasswordForm = this.formBuilder.group({
      code: new FormControl('', [Validators.required, Validators.pattern(this.mfaCodePattern)]),
      newPassword: new FormControl('', [Validators.required, Validators.pattern(this.passwordPattern)]),
      confirmNewPassword: new FormControl('', [Validators.required, Validators.pattern(this.passwordPattern)])
    });
  }

  /**
   * Subscribe forgot password actions
   * @method subscribeForgotPassword
   */
  private subscribeForgotPassword(): void {
    // Subscribe forgot password
    this.store.select(Selectors.auth.forgotPassword)
      .pipe(
        takeUntil(this.unsubscribe$)
      ).subscribe((response: any) => {
        if (response) {
          this.isReset = true;
        }
      });
  }

  /**
   * Returns flag show materror or no
   * @method showFormControlErrors
   */
  showFormControlErrors(form: FormGroup, formControlName: string): boolean {
    return form.get(formControlName).invalid && (form.get(formControlName).dirty || form.get(formControlName).touched);
  }

  /**
   * Forgot password
   * @method forgotPassword
   */
  forgotPassword(): void {
    if (this.forgotPasswordForm.invalid) {
      return;
    }

    this.store.dispatch(new AuthActions.ForgotPassword({ email: this.forgotPasswordForm.value.username }));
  }

  /**
   * Reset password
   * @method resetPassword
   */
  resetPassword(): void {
    if (this.resetPasswordForm.invalid) {
      return;
    }
    if ((this.resetPasswordForm.value.newPassword !== this.resetPasswordForm.value.confirmNewPassword)) {
      this.snackbar.error(this.translateService.instant('snack_message.new_and_conirmation_passwords_do_not_match'));
      return;
    }

    const payload: IResetPassword = {
      ...this.resetPasswordForm.value,
      username: this.forgotPasswordForm.value.username
    };

    this.store.dispatch(new AuthActions.ResetPassword(payload));
  }
}
